/**
 * Reading back WHY the last sync on an account found nothing.
 *
 * WHAT THIS CLOSES. The owner's complaint, twice, was "ทำไม data facebook ไม่
 * sync เลย" and "sync แล้วไม่มีข้อมูล" — why is there no data. By the time this
 * module was written the answer already existed, and precisely: the history
 * probe writes "the platform reports 0 lifetime spend here, so there is nothing
 * to fetch … and no older window will hold anything either" onto the job, and
 * decideJobOutcome writes "the platform returned nothing for this range, which
 * is what a dormant account looks like" for the other case. Durable in
 * `sync_jobs.error_message`, durable in the worker log — and read by NO screen.
 * `getSyncStatus` passes the column through only for `status === 'failed'`, and
 * `systemHealth` only looks at `failed` too, so a `no_data` note had never
 * reached a page.
 *
 * WHY A SEPARATE READER rather than widening `getSyncStatus`. That function is
 * polled, per account, only while a sync this session started is in flight — so
 * anything it learned would vanish on reload, which is exactly when the
 * question gets asked. This one is loaded WITH the account list, so the reason
 * sits beside the row that shows nothing, on a cold page load, days later.
 *
 * @module services/lastSyncOutcome
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";
import { ACTIVE_JOB_STATUSES } from "./syncJobShared";

/** accountId -> the note the last finished run left, for runs that found nothing. */
export type EmptySyncReasons = ReadonlyMap<string, string>;

/**
 * Whether a finished run owes the reader an explanation, and what it is.
 *
 * Only `no_data`. The other three are deliberate, each for its own reason:
 *
 *   - `success` and `partial` wrote rows. The frontier on the right of the row
 *     moved, so "why is there nothing" is not the question being asked. A
 *     throttled `partial` writes nothing and does carry a real sentence about a
 *     window left unread — that is a different claim ("come back shortly"), not
 *     a durable fact about the account, and putting it in the same slot would
 *     conflate the two.
 *   - `failed` already has a surface, and a loud one: `accountSyncError` writes
 *     it to the account, `derivePassiveStatus` reads it, and the row draws the
 *     reconnect banner with the message in full. Repeating it here would put
 *     the same sentence on the row twice.
 *
 * A blank note yields null rather than an empty disclosure — a reader who opens
 * one and finds nothing inside has been told the page is broken.
 */
export const emptyRunReason = (status: string, note: string | null): string | null =>
  status === "no_data" && note?.trim() ? note.trim() : null;

/**
 * The latest FINISHED job per account, reduced to the accounts that need a
 * reason.
 *
 * One query per account, like `fetchPlatformFrontiers` beside it, and for a
 * sharper reason than symmetry: a single ordered read across the workspace
 * would be capped, and the real incident was 140 backfill jobs on three ad
 * accounts of ONE workspace — enough for one busy account to fill any page and
 * starve the others of an answer. `limit(1)` per account cannot be starved.
 *
 * Queued and running rows are excluded. A job that has not run explains
 * nothing, and it would otherwise be a candidate for "the latest" — hiding the
 * note from the run that actually happened behind one that has not.
 *
 * ORDERED BY WHEN THE RUN FINISHED, not by when the row was written, and that
 * is not a nicety. MEASURED on the local database: a backfill inserts its
 * months in ONE statement, so 25 finished jobs on a single account carry an
 * identical `created_at` — and on one of them those 25 rows held three
 * different statuses. `order('created_at').limit(1)` therefore picks
 * arbitrarily among 25 rows, and the two keys genuinely disagreed on account
 * 9c31de90: `created_at` answered `success`, `completed_at` answers `no_data`.
 * A row whose reason is decided by a tie is worse than no reason at all.
 * `completed_at` separates them — siblings a probe closes share one timestamp,
 * a chunk that actually ran finishes later. It is non-null on every terminal
 * row today (513 of 513); `nullsFirst: false` and the `created_at` tie-break
 * are for the row that one day is not, because Postgres sorts NULLS FIRST on
 * DESC and such a row would win every time.
 *
 * A failed lookup is logged and dropped, never thrown: this is an explanation
 * printed beside the account list, and one unreadable row must not blank the
 * list the reader came for. Absent means "nothing to add", which is what the
 * row said before this existed.
 */
export async function fetchEmptySyncReasons(
  accountIds: readonly string[],
): Promise<EmptySyncReasons> {
  if (accountIds.length === 0) return new Map();

  const supabase = getSupabaseClient();
  const found = await Promise.all(
    accountIds.map(async (accountId) => {
      const { data, error } = await supabase
        .from("sync_jobs")
        .select("status, error_message")
        .eq("account_id", accountId)
        // Built from the one existing constant rather than a fresh list of
        // terminal statuses: a second vocabulary here would have to be kept in
        // step with migration 103's by somebody who knew both existed.
        .not("status", "in", `(${ACTIVE_JOB_STATUSES.join(",")})`)
        .order("completed_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logError("LastSyncOutcome", `Could not read the last sync outcome for ${accountId}`, error);
        return null;
      }
      const row = data as { status: string; error_message: string | null } | null;
      if (!row) return null;
      const reason = emptyRunReason(row.status, row.error_message);
      return reason ? ([accountId, reason] as const) : null;
    }),
  );

  return new Map(found.filter((entry): entry is readonly [string, string] => entry !== null));
}
